import { defineStore } from 'pinia'
import { ref } from 'vue'
import { playNotificationSound } from '@/utils/notification-sound'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
	id: string
	type: NotificationType
	message: string
	/** Optional bold context line above the message (e.g. "Salesforce") */
	title?: string
	/** ms until auto-dismiss; <= 0 means sticky (close button only). Resolved from per-type defaults when omitted. */
	timeout: number
	actionLabel?: string
	onAction?: () => void
	/** @deprecated toasts render in a fixed top-right stack; kept for API compatibility */
	location?: string
	/** How many identical messages were coalesced into this toast */
	count: number
	/** Bumped when a duplicate arrives so the countdown/progress restarts */
	revision: number
}

export type NotificationInput = Omit<Notification, 'id' | 'timeout' | 'count' | 'revision'> & {
	timeout?: number
}

// Errors get the longest read time; successes confirm and get out of the way
const DEFAULT_TIMEOUTS: Record<NotificationType, number> = {
	success: 6000,
	info: 6000,
	warning: 6000,
	error: 8000,
}

const MAX_TOASTS = 5

export const useNotificationStore = defineStore('notification', () => {
	const notifications = ref<Notification[]>([])

	// Auto-dismiss timing lives in NotificationToast (it owns hover-pause);
	// the store only manages the queue.
	function add(notification: NotificationInput): string {
		// Soft audible cue whenever a toast is raised (new or a coalesced repeat).
		// Fire-and-forget and self-guarding — never let audio affect the queue.
		playNotificationSound()

		// Coalesce identical visible messages into one toast with a counter
		// instead of stacking clones
		const existing = notifications.value.find(
			(n) =>
				n.type === notification.type &&
				n.message === notification.message &&
				n.title === notification.title,
		)
		if (existing) {
			existing.count++
			existing.revision++
			return existing.id
		}

		const id = crypto.randomUUID()
		notifications.value.push({
			...notification,
			id,
			timeout: notification.timeout ?? DEFAULT_TIMEOUTS[notification.type],
			count: 1,
			revision: 0,
		})

		// Stack is capped: the oldest dismissible toast collapses to make room
		// (sticky toasts are spared unless everything is sticky)
		while (notifications.value.length > MAX_TOASTS) {
			const oldest = notifications.value.find((n) => n.timeout > 0) ?? notifications.value[0]
			remove(oldest.id)
		}

		return id
	}

	function remove(id: string) {
		notifications.value = notifications.value.filter((n) => n.id !== id)
	}

	function $reset() {
		notifications.value = []
	}

	return { notifications, add, remove, $reset }
})
